type Tone = "brand" | "ok" | "warn" | "danger" | "muted";

interface SparklineProps {
  values: number[] | null | undefined;
  width?: number;
  height?: number;
  tone?: Tone;
  fill?: boolean;
}

const TONE_VAR: Record<Tone, string> = {
  brand: "var(--brand)",
  ok: "var(--ok)",
  warn: "var(--warn)",
  danger: "var(--danger)",
  muted: "var(--ink-faint)",
};

/**
 * Tiny inline SVG sparkline. Renders a faint dashed baseline when there's
 * no data so the row layout doesn't shift.
 */
export function Sparkline({
  values,
  width = 96,
  height = 24,
  tone = "brand",
  fill = false,
}: SparklineProps) {
  if (!values || values.length === 0) {
    return (
      <svg width={width} height={height} className="sparkline sparkline--empty">
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="var(--edge-strong)"
          strokeDasharray="3 3"
          strokeWidth={1}
        />
      </svg>
    );
  }
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 1e-9);
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = stepX * i;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const pathD = `M${points.join(" L")}`;
  const areaD = fill
    ? `${pathD} L${width.toFixed(2)},${height} L0,${height} Z`
    : null;
  const stroke = TONE_VAR[tone];
  return (
    <svg
      width={width}
      height={height}
      className="sparkline"
      role="img"
      aria-label="sparkline"
    >
      {areaD && <path d={areaD} fill={stroke} fillOpacity={0.12} />}
      <path d={pathD} stroke={stroke} strokeWidth={1.4} fill="none" />
    </svg>
  );
}
